// libraries
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// slices
import {
  setWebsocketError,
  setWebsocketLoading,
  setWebsocketResponses,
} from '../slices/webSocketGeoref.slice';

import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import { CountryCode } from '@domains/country';

interface UseCustomWebSocketProps {
  whenSuccessGeoref: () => void;
}

export interface CustomPayloadMX {
  country: string;
  agent: string;
  area: number;
  room_num: number;
  garage: number;
  elevator: number;
  bath: number;
  half_bathroom: number;
  latitude: number;
  longitude: number;
  years_old: number;
  floor_num: number;
  land_area: number;
  address: string;
  property_type_id: number;
  price_type: string;
  validations: boolean;
  exterior_number: string;
  mex_state_id: string;
  zip_code: string;
  deal_id: number;
  hubspot_ignore: string[];
}

const selectPutLeadServiceForCustom = (country: CountryCode | undefined) => {
  switch (country) {
    case 'MX':
      return 'put_lead_mx';
    case 'CO':
      return 'put_lead_co';
    default:
      return 'put_lead_mx';
  }
};

const useCustomWebSocket = ({ whenSuccessGeoref }: UseCustomWebSocketProps) => {
  const dispatch = useDispatch();

  const countryCode = useSelector(getCountry);
  const customGeorefService = selectPutLeadServiceForCustom(countryCode);

  const customWebsocketResponsesRef = useRef<any[]>([]);
  const pendingRequestRef = useRef(false);

  const [customWs, setCustomWs] = useState<WebSocket | null>(null);

  useEffect(() => {
    const customWebsocket = new WebSocket(
      process.env.ENV_WEBSOCKET_BACKBONE as string
    );
    setCustomWs(customWebsocket);

    return () => {
      customWebsocket.close();
    };
  }, []);

  useEffect(() => {
    if (!customWs) return;

    customWs.onopen = () => {
      console.log('WebSocket conectado (Custom)');
    };

    customWs.onmessage = async customEvent => {
      if (!pendingRequestRef.current) {
        console.log('⚠️ Mensaje del backbone ignorado (CustomWS): no hay petición pendiente');
        return;
      }
      // NO resetear pendingRequestRef aquí — el backbone envía múltiples mensajes
      // (ej. "put_lead" primero, "georeferencing" después). Solo se resetea cuando
      // llega el mensaje final (georeferencing success/failure).

      dispatch(setWebsocketLoading(true));

      try {
        const customParsedData = JSON.parse(customEvent.data);
        if (customParsedData?.body) {
          const customParsedBody = JSON.parse(customParsedData.body);

          console.log('[CustomWebSocket] 📦 parsedBody del backbone:', {
            step: customParsedBody?.step,
            success: customParsedBody?.success,
          });

          if (
            customParsedBody?.step === 'georeferencing' &&
            customParsedBody?.success
          ) {
            // Mensaje final: resetear el flag y redirigir
            pendingRequestRef.current = false;
            console.log('[CustomWebSocket] 🎉 Georeferencing exitoso → whenSuccessGeoref');
            dispatch(setWebsocketLoading(false));
            try {
              whenSuccessGeoref();
            } catch (customError) {
              console.error('Error en georreferenciación (Custom WS):', customError);
              dispatch(setWebsocketError(true));
            }
          } else if (
            customParsedBody?.step === 'georeferencing' &&
            customParsedBody?.success === false
          ) {
            // Mensaje final (error): resetear el flag
            pendingRequestRef.current = false;
            console.error('[CustomWebSocket] ❌ Georeferencing fallido');
            dispatch(setWebsocketError(true));
            dispatch(setWebsocketLoading(false));
          } else {
            // Mensaje intermedio (ej. put_lead): mantener pendingRequestRef = true
            console.log('[CustomWebSocket] ⏳ Mensaje intermedio (step:', customParsedBody?.step, ') — esperando georeferencing...');
            dispatch(setWebsocketLoading(false));
          }

          customWebsocketResponsesRef.current.push(customParsedBody);
        }
      } catch (customParseError) {
        console.error(
          'Error parseando la data del WS (Custom):',
          customParseError
        );
        dispatch(setWebsocketError(true));
        dispatch(setWebsocketLoading(false));
      }
    };

    customWs.onerror = customError => {
      console.log('WebSocket error (Custom):', customError);
      pendingRequestRef.current = false;
      dispatch(setWebsocketError(true));
      dispatch(setWebsocketLoading(false));
    };

    customWs.onclose = () => {
      console.log('WebSocket desconectado (Custom), reintentando...');
      pendingRequestRef.current = false;
      setCustomWs(new WebSocket(process.env.ENV_WEBSOCKET_BACKBONE as string));
      dispatch(setWebsocketLoading(false));
    };
  }, [customWs, dispatch, whenSuccessGeoref]);

  const sendCustomMessage = (payload: CustomPayloadMX) => {
    if (!customWs) return;
    if (!payload.deal_id) return;

    const customAgente = payload.agent;

    console.log('[CustomWebSocket] 📤 sendCustomMessage — ENVIANDO al backbone:', {
      action: customGeorefService,
      deal_id: payload.deal_id,
      validations: payload.validations,
      wsReadyState: customWs.readyState,
      body: { ...payload, agente: customAgente },
    });

    const customMessageBody = JSON.stringify({
      action: customGeorefService,
      Authorization: 'default',
      body: JSON.stringify({
        ...payload,
        agente: customAgente,
      }),
    });

    pendingRequestRef.current = true;
    customWs.send(customMessageBody);
  };

  const sendRealEstatePayload = (payload: CustomPayloadMX) => {
    // Construir el objeto con la estructura exacta que necesitas
    const customPayload: CustomPayloadMX = {
      country: payload.country,
      agent: payload.agent,
      area: payload.area,
      room_num: payload.room_num,
      garage: payload.garage,
      elevator: payload.elevator,
      bath: payload.bath,
      half_bathroom: payload.half_bathroom,
      latitude: payload.latitude,
      longitude: payload.longitude,
      years_old: payload.years_old,
      floor_num: payload.floor_num,
      land_area: payload.land_area,
      address: payload.address,
      property_type_id: payload.property_type_id,
      price_type: payload.price_type,
      validations: payload.validations,
      exterior_number: payload.exterior_number,
      mex_state_id: payload.mex_state_id,
      zip_code: payload.zip_code,
      deal_id: payload.deal_id,
      hubspot_ignore: payload.hubspot_ignore,
    };

    // Enviar directamente al websocket
    sendCustomMessage(customPayload);
  };

  useEffect(() => {
    if (customWebsocketResponsesRef.current.length > 0) {
      dispatch(setWebsocketResponses(customWebsocketResponsesRef.current));
    }
  }, [customWebsocketResponsesRef.current.length, dispatch]);

  return {
    sendRealEstatePayload,
    sendCustomMessage,
  };
};

export default useCustomWebSocket;
